/**
 * The Excel workbook — a WORKING FILE FOR PEOPLE (DEC-104, `docs/adr/9041`). The CSV beside it
 * stays the raw machine file (G2.4, `csv.ts`), unchanged.
 *
 * DEC-98 made the workbook the CSV's table in typed cells: machine headers (`net_benefit`), codes
 * (`approve`), a `*_currency` column beside every amount, fifteen provenance columns repeated on
 * every row, UTC times. Correct for a program and unreadable for the merchandiser who opens it.
 * DEC-104 keeps the CSV exactly that and lays the workbook out for a person instead:
 *
 *   header       the column's catalogue header in the user's locale, the unit in words; an amount
 *                says its currency once, «Net benefit (COP)», when the sheet has one currency (the
 *                currency column stays where amounts mix). No translation → the machine key,
 *                listed in `fallbacks`
 *   codes        the column's `display` hook turns a code into words; a label cell shows its label
 *   numbers      NUMERIC cells at full precision, shown through a number format (`#,##0`, `#,##0.0`,
 *                money at its currency's ISO 4217 digits, `0.0%`) — Excel draws the separators of
 *                the reader's own system. Never a display string in a cell
 *   dates        DATE cells in the user's local time zone (`timeZone`, the browser's by default),
 *                in the locale's day/month/year order; the «Tecnico» sheet names the zone
 *   true/false   «Sì»/«No», in the user's locale
 *   absent       «n.d.» — a `not-reported` cell, or an empty cell in a numeric column. The REASON is
 *                in its `…_absent_reason` column, which moves to «Tecnico»
 *   counted zero a real 0 (reqs/00 D8); `not-ranged` is its words — «Non assortito» — unless the
 *                column says it means "does not apply" there (`absent.notRanged`): then a blank, or
 *                «n.d.», with the reason in «Tecnico» (`absent.reasonKeys`), or the reason's own
 *                words in the cell (`reason`)
 *
 * Every data sheet: the header bold and frozen, the leading `freeze` columns frozen with it, an
 * autofilter over the header, and — when a column declares `total: 'sum'` — a totals row under
 * the filtered range. One «Tecnico» sheet, last: the nine provenance fields and the run's
 * provenance columns (ADR 9120) written ONCE, the time zone, then each data sheet's technical
 * columns (ids, codes, absent reasons) keyed by the data row's number and its `identifies` column.
 * Every workbook ends with a «Colonne» sheet after it: every column of every sheet, grouped by
 * sheet, with its header, machine name, unit and meaning (`docs/adr/9045`). A column that has no
 * meaning written says so in words — «Descrizione non ancora scritta» — and is never left blank.
 *
 * **No formula from data, ever.** Every text is a shared-string cell (`t="s"`): `=cmd|' /C calc'!A0`
 * arrives as the characters it is. The one formula this writer emits is the totals row's
 * `SUBTOTAL(109,L2:L41)`, assembled here from a column letter and two row numbers — `XlsxCell`'s
 * `subtotal` variant carries a range it computed, and no string a builder supplied can reach it.
 *
 * **The file is written as a stream, not built** (`docs/adr/9073`). This module plans the workbook —
 * what every header says, the widths, the notes — and hands the rows over one at a time, as events;
 * `xlsx-package.ts` writes them into a zip as they come (`fflate`, MIT), on a worker thread where the
 * browser has one. A run's every-cell file is 436,950 rows of sixty columns, and a library that builds
 * the workbook in memory (the first writer here, `write-excel-file`) ran the tab out of memory long
 * after the file had arrived. The parts a stream needs to be independent are in `xlsx-parts.ts`; the
 * writer is imported on demand, so the first paint does not carry it.
 */

import type { ExportCell, ExportColumn, ExportRequest, ExportTranslate, WorkbookSheet } from './types';
import { fileProvenanceRows } from './provenance';
import { columnLetter } from './xlsx-parts';

export { columnLetter, commentsXml, vmlDrawingXml } from './xlsx-parts';

export { XLSX_MIME } from './xlsx-parts';

/** The fallback names, used when a translated name is empty once made Excel-safe. */
export const SHEET_NAMES = {
  data: 'Data',
  technical: 'Technical',
  columns: 'Columns',
} as const;

export interface XlsxOptions {
  /** Resolves an ICU key to text, injected so this module holds no strings (G6). */
  readonly translate: ExportTranslate;
  /** BCP-47 tag: the date order. Numbers carry a format code; Excel draws the reader's separators. */
  readonly locale?: string;
  /** IANA zone the dates are written in — the user's. Defaults to the browser's own. */
  readonly timeZone?: string;
  /** The rows written so far, across every sheet, now and then while a large workbook is written. */
  readonly onProgress?: (rows: number) => void;
}

// ── the workbook as data, before any library sees it ─────────────────────────────

/** One workbook cell, typed. The only formula is a `subtotal` over a range this module computed. */
export type XlsxCell =
  | { readonly type: 'number'; readonly value: number; readonly format?: string }
  | { readonly type: 'date'; readonly value: Date; readonly format: string }
  | { readonly type: 'text'; readonly value: string }
  | { readonly type: 'blank' }
  | { readonly type: 'subtotal'; readonly range: string; readonly format?: string };

/** A sheet before its rows: everything the writer needs to open it. */
export interface XlsxSheetHead {
  readonly name: string;
  /** Written bold, and frozen above the rows. */
  readonly header: readonly string[];
  /** Column widths, in characters. */
  readonly widths: readonly number[];
  /** Columns frozen at the left, with the header. */
  readonly frozenColumns: number;
  /** What each header cell means, as an Excel note (`docs/adr/9045`'s amendment of 2026-10-07). */
  readonly notes: readonly XlsxNote[];
}

/** A whole sheet, every row held — what {@link buildXlsxWorkbook} gives the tests. */
export interface XlsxSheet extends XlsxSheetHead {
  readonly rows: readonly (readonly XlsxCell[])[];
  /** The autofilter's range (`A1:F41`), or `null` for none. */
  readonly autoFilter: string | null;
  /** Indexes into `rows` written bold — a totals row, a block's title and header. */
  readonly boldRows: ReadonlySet<number>;
}

/**
 * One header cell's explanation, shown as an Excel NOTE — the yellow box that appears when the
 * pointer rests on the cell, marked by a red triangle in its corner.
 *
 * The «Colonne» sheet already carried every column's meaning, and a planner had to leave the data
 * to read it (client request, 2026-10-07: «così diventa anche più semplice da consultare senza
 * dover muoversi tra due fogli»). The note puts the same text on the header itself; the sheet stays
 * as the searchable, printable index of the whole file, and the two are written from one source
 * (`columnMeaning`), so they cannot drift.
 *
 * **A note, not a data-validation input message.** The other way to get a tooltip into a cell is a
 * `dataValidation` with `showInputMessage`, which needs no extra parts — and whose `prompt` Excel
 * caps at 255 characters. 46 of this product's 313 column explanations are longer, the longest
 * being `bound_by` at 890, which is the column an allocator asks about most. A mechanism that
 * silently truncates the explanation that matters most is not a mechanism for explaining columns.
 */
export interface XlsxNote {
  /** The header cell's column, 0-based, into `XlsxSheet.header`. */
  readonly at: number;
  /** The meaning, in the reader's language, whole. */
  readonly text: string;
}

export interface XlsxWorkbook {
  readonly sheets: readonly XlsxSheet[];
  /** Columns whose header had no translation and fell back to the machine key, as `sheet:key`. */
  readonly fallbacks: readonly string[];
}

/**
 * What the workbook's rows are, in the order they are written. A `row` goes under the header (and the
 * rows before it) of sheet `sheet`, an index into {@link XlsxPlan.sheets}; the `end` of a sheet says its
 * autofilter, which is known only once its rows have been counted. Rows of different sheets interleave:
 * a source row is both a row of its data sheet and a row of «Tecnico».
 */
export type XlsxEvent =
  | { readonly kind: 'row'; readonly sheet: number; readonly cells: readonly XlsxCell[]; readonly bold: boolean }
  | { readonly kind: 'end'; readonly sheet: number; readonly autoFilter: string | null };

/** The workbook, planned: its sheets' heads, and its rows as a stream. See {@link planXlsx}. */
export interface XlsxPlan {
  readonly sheets: readonly XlsxSheetHead[];
  /** Columns whose header had no translation and fell back to the machine key, as `sheet:key`. */
  readonly fallbacks: readonly string[];
  /** The rows. Called once per file written; reads the request's rows again each time. */
  readonly events: () => AsyncGenerator<XlsxEvent>;
}

/** Excel's own limit on a sheet name. */
export const SHEET_NAME_MAX = 31;

/** Shorter than this, a word-boundary cut keeps too little — cut at the limit instead. */
const SHEET_NAME_MIN_WORD_CUT = 12;

/**
 * An Excel-safe sheet name: none of `[ ] : * ? / \`, no leading or trailing apostrophe, at
 * most 31 characters — cut at a word boundary, never mid-word, when one leaves enough —, not
 * Excel's reserved `History`, and unique (case-insensitively) among `taken`. Empty after
 * cleaning: `fallback`.
 */
export function sheetName(raw: string, fallback: string, taken: ReadonlySet<string> = new Set()): string {
  const clean = (value: string): string => value
    .replace(/[[\]:*?/\\]/g, ' ')
    // Control characters are not allowed either.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^'+|'+$/g, '')
    .trim();
  const cut = (value: string, max: number): string => {
    if (value.length <= max) return value;
    const head = value.slice(0, max + 1);
    const space = head.lastIndexOf(' ');
    return (space >= SHEET_NAME_MIN_WORD_CUT ? head.slice(0, space) : value.slice(0, max)).trim();
  };
  let base = cut(clean(raw), SHEET_NAME_MAX);
  if (base === '' || base.toLowerCase() === 'history') base = cut(clean(fallback), SHEET_NAME_MAX) || 'Sheet';
  const lower = new Set([...taken].map((name) => name.toLowerCase()));
  let name = base;
  for (let n = 2; lower.has(name.toLowerCase()); n += 1) {
    const suffix = ` (${n})`;
    name = `${base.slice(0, SHEET_NAME_MAX - suffix.length).trimEnd()}${suffix}`;
  }
  return name;
}

/**
 * A column that holds identifiers rather than quantities — a key, an id, a code, an EAN. A
 * number in such a column is written as TEXT: it is a label that happens to be digits, and as a
 * number Excel would drop its leading zeros, show it in scientific notation past eleven digits
 * and round it past fifteen.
 */
export function isIdentifierColumn(column: ExportColumn): boolean {
  return /(^|_)(id|key|code|ean|gtin|upc|sku|barcode)$/i.test(column.key);
}

/** Moved to the «Tecnico» sheet: declared, or a reason column by its name. */
export function isTechnicalColumn(column: ExportColumn): boolean {
  return column.technical === true || /_absent_reason$/.test(column.key);
}

/** Beyond this an integer loses digits in Excel (15 significant digits). */
const EXCEL_EXACT_INTEGER = 1e15;

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;

/**
 * An ISO-8601 date or date-time as the instant it names, in UTC. A date-time without an offset
 * is read as UTC rather than as the browser's local time — the platform writes UTC. Anything
 * else — a week code, a partial date — is not a date here, and leaves as the text it is.
 */
export function isoToUtcDate(iso: string): { readonly value: Date; readonly time: boolean } | null {
  const day = DATE_ONLY.exec(iso);
  if (day) {
    const value = new Date(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3])));
    // `2026-02-31` rolls over in `Date.UTC`; a date that is not the one written is not a date.
    return value.toISOString().slice(0, 10) === iso ? { value, time: false } : null;
  }
  if (!DATE_TIME.test(iso)) return null;
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const value = new Date(hasOffset ? iso : `${iso}Z`);
  return Number.isNaN(value.getTime()) ? null : { value, time: true };
}

const CLOCKS = new Map<string, Intl.DateTimeFormat>();

/** One formatter per zone: building one costs more than formatting with it, and a large file formats a million dates. */
function clockFormat(timeZone: string): Intl.DateTimeFormat {
  let format = CLOCKS.get(timeZone);
  if (format === undefined) {
    format = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    CLOCKS.set(timeZone, format);
  }
  return format;
}

/**
 * The wall-clock time `instant` shows in `timeZone`, as a Date whose UTC fields ARE that clock.
 * An Excel cell holds no zone: it holds the clock, so the clock written is the user's.
 */
export function wallClock(instant: Date, timeZone: string): Date {
  const parts = clockFormat(timeZone).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return new Date(Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second')));
}

/**
 * The locale's day/month/year order as an Excel date format — `dd/mm/yyyy` for it-IT, en-GB and
 * es-419, `mm/dd/yyyy` for en-US — read from `Intl`, not written per locale.
 */
export function dateFormatFor(locale: string): string {
  const parts = new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' })
    .formatToParts(new Date(Date.UTC(2001, 10, 22)));
  const code = parts.map((p) => {
    if (p.type === 'day') return 'dd';
    if (p.type === 'month') return 'mm';
    if (p.type === 'year') return 'yyyy';
    return p.value.replace(/[^/.\- ]/g, '');
  }).join('');
  return /dd/.test(code) && /mm/.test(code) && /yyyy/.test(code) ? code : 'yyyy-mm-dd';
}

/**
 * A currency's ISO 4217 minor-unit digits; 2 for a code the list does not single out. ISO's, not
 * `Intl`'s: CLDR 48 (Node 22's ICU, and browsers as they update) draws COP with 0 decimals while
 * ISO keeps 2, so asking `Intl` made the same file differ by runtime. ADR 9041 names ISO; a column
 * that wants fewer says so with `decimals`.
 */
export function currencyDigits(currency: string): number {
  const code = currency.toUpperCase();
  if (ISO_4217_ZERO_DIGITS.has(code)) return 0;
  if (ISO_4217_THREE_DIGITS.has(code)) return 3;
  return 2;
}

/** ISO 4217 currencies whose minor unit is 0 — the list's exceptions to 2. */
const ISO_4217_ZERO_DIGITS: ReadonlySet<string> = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'UYI', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

/** ISO 4217 currencies whose minor unit is 3. */
const ISO_4217_THREE_DIGITS: ReadonlySet<string> = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']);

/** Excel's 1900 date system misplaces a date before 1 March 1900; such a date leaves as text. */
const EXCEL_EPOCH = Date.UTC(1900, 2, 1);

/** A unit as words: the catalogue's, or nothing for a unit a person does not read («boolean»). */
const UNIT_KEYS: Readonly<Record<string, string | null>> = {
  pairs: 'export.unit.pairs',
  units: 'export.unit.units',
  weeks: 'export.unit.weeks',
  wk: 'export.unit.weeks',
  days: 'export.unit.days',
  'pairs/week': 'export.unit.pairsPerWeek',
  'pairs/wk': 'export.unit.pairsPerWeek',
  'store-weeks': 'export.unit.storeWeeks',
  periods: 'export.unit.periods',
  seconds: 'export.unit.seconds',
  cells: 'export.unit.cells',
  count: 'export.unit.count',
  share: 'export.unit.share',
  ratio: 'export.unit.ratio',
  index: 'export.unit.index',
  percentagePoints: 'export.unit.percentagePoints',
  actions: 'export.unit.actions',
  boolean: null,
  identifier: null,
  date: null,
  code_list: null,
  currency: null,
  money: null,
};

const NOT_AVAILABLE_KEY = 'export.cell.notAvailable';
const NOT_RANGED_KEY = 'export.cell.notRanged';
const YES_KEY = 'export.boolean.yes';
const NO_KEY = 'export.boolean.no';
const TOTAL_KEY = 'export.totals';

/** Matches an ICU placeholder left unfilled: `{currency}`, `{count, plural, …}`. */
const UNFILLED_PLACEHOLDER = /\{\s*\w+\s*[,}]/;

interface Context {
  readonly translate: ExportTranslate;
  readonly dateFormat: string;
  readonly timeZone: string;
  /** What the catalogue says for a key that takes no values, said once: a large file asks for «n.d.» a million times. */
  readonly plain: Map<string, string | null>;
  /** What a column's `display` hook said for a code, once. */
  readonly worded: WeakMap<ExportColumn, Map<string, string>>;
}

function newContext(translate: ExportTranslate, dateFormat: string, timeZone: string): Context {
  return { translate, dateFormat, timeZone, plain: new Map(), worded: new WeakMap() };
}

/** {@link translated} for a key with no values, remembered for the rest of the workbook. */
function say(context: Context, key: string): string | null {
  let text = context.plain.get(key);
  if (text === undefined) { text = translated(context.translate, key); context.plain.set(key, text); }
  return text;
}

/** The catalogue's text for `key`, or `null` when there is none worth showing. */
export function translated(translate: ExportTranslate, key: string, values?: Readonly<Record<string, unknown>>): string | null {
  const text = values === undefined ? translate(key) : translate(key, values);
  return text === '' || text === key || UNFILLED_PLACEHOLDER.test(text) ? null : text;
}

/** A column's unit as words in the reader's language, or `null` for none (or one a person does not read). */
export function unitWords(unit: string | undefined, translate: ExportTranslate): string | null {
  if (unit === undefined || unit === '') return null;
  const key = UNIT_KEYS[unit];
  if (key === null) return null;
  if (key === undefined) return unit;
  return translated(translate, key) ?? unit;
}

const isNumericKind = (column: ExportColumn): boolean => (
  column.kind === 'number' || column.kind === 'money' || column.kind === 'percent' || column.kind === 'integer'
);

const FORMATS = new WeakMap<ExportColumn, Map<string | null, string>>();

/** The number format a numeric column is shown in; the cell keeps its full precision. Said once per column and currency. */
function numberFormat(column: ExportColumn, currency: string | null): string {
  let byCurrency = FORMATS.get(column);
  if (byCurrency === undefined) { byCurrency = new Map(); FORMATS.set(column, byCurrency); }
  let format = byCurrency.get(currency);
  if (format === undefined) { format = formatFor(column, currency); byCurrency.set(currency, format); }
  return format;
}

function formatFor(column: ExportColumn, currency: string | null): string {
  const digits = (count: number): string => (count > 0 ? `.${'0'.repeat(count)}` : '');
  let format: string;
  switch (column.kind) {
    case 'integer': format = `#,##0${digits(column.decimals ?? 0)}`; break;
    case 'money': format = `#,##0${digits(column.decimals ?? (currency === null ? 2 : currencyDigits(currency)))}`; break;
    case 'percent': format = `0${digits(column.decimals ?? 1)}%`; break;
    default: format = `#,##0${digits(column.decimals ?? 1)}`;
  }
  return column.zeroAsBlank === true ? `${format};-${format};` : format;
}

function numberCell(raw: number, column: ExportColumn, format: string): XlsxCell {
  if (!Number.isFinite(raw)) return { type: 'text', value: String(raw) };
  if (isIdentifierColumn(column)) return { type: 'text', value: String(raw) };
  if (Number.isInteger(raw) && Math.abs(raw) >= EXCEL_EXACT_INTEGER) return { type: 'text', value: String(raw) };
  return { type: 'number', value: raw, format };
}

function dateCell(iso: string, context: Context): XlsxCell {
  const parsed = isoToUtcDate(iso);
  if (parsed === null || parsed.value.getTime() < EXCEL_EPOCH) return { type: 'text', value: iso };
  return parsed.time
    ? { type: 'date', value: wallClock(parsed.value, context.timeZone), format: `${context.dateFormat} hh:mm` }
    : { type: 'date', value: parsed.value, format: context.dateFormat };
}

/** Distinct codes remembered per column: a column of enumerations fits, a column of free text is not remembered without end. */
const MAX_WORDED_CODES = 5000;

function words(code: string, column: ExportColumn, context: Context): string {
  const display = column.display;
  if (display === undefined) return code;
  let byCode = context.worded.get(column);
  if (byCode === undefined) { byCode = new Map(); context.worded.set(column, byCode); }
  let text = byCode.get(code);
  if (text === undefined) {
    text = display(code, context.translate);
    if (byCode.size < MAX_WORDED_CODES) byCode.set(code, text);
  }
  return text;
}

const notAvailable = (context: Context): XlsxCell => ({
  type: 'text', value: say(context, NOT_AVAILABLE_KEY) ?? 'n/a',
});

/** One cell as a person reads it. */
function peopleCell(cell: ExportCell, column: ExportColumn, currency: string | null, context: Context): XlsxCell {
  switch (cell.kind) {
    case 'number': case 'money': case 'percent':
      return numberCell(cell.raw, column, numberFormat(column, cell.kind === 'money' ? cell.currency : currency));
    case 'integer':
      return numberCell(Math.trunc(cell.raw), column, numberFormat(column, currency));
    case 'date':
      return dateCell(cell.iso, context);
    case 'text': {
      if (cell.value === '') return isNumericKind(column) ? notAvailable(context) : { type: 'blank' };
      if (cell.value === 'true') return { type: 'text', value: say(context, YES_KEY) ?? 'true' };
      if (cell.value === 'false') return { type: 'text', value: say(context, NO_KEY) ?? 'false' };
      return { type: 'text', value: words(cell.value, column, context) };
    }
    case 'label':
      if (column.display !== undefined) return { type: 'text', value: words(cell.code, column, context) };
      // A yes/no carried as a label reads as words too, as a text cell's does (usability audit E3).
      if (cell.value === 'true') return { type: 'text', value: say(context, YES_KEY) ?? 'true' };
      if (cell.value === 'false') return { type: 'text', value: say(context, NO_KEY) ?? 'false' };
      return { type: 'text', value: cell.value };
    // reqs/00 D8: `counted-zero` is a real zero; the other two are not numbers and say so.
    case 'missing':
      if (cell.state === 'counted-zero') return numberCell(0, column, numberFormat(column, currency));
      if (cell.state === 'not-reported') return notAvailable(context);
      // `not-ranged` is the assortment state only where the column does not say otherwise: a
      // pending line's decision time is not «Non assortito» (signed-in check, 2026-10-02).
      if (column.absent?.notRanged === 'blank') return { type: 'blank' };
      if (column.absent?.notRanged === 'notAvailable') return notAvailable(context);
      if (column.absent?.notRanged === 'reason') {
        const key = column.absent.reasonKeys?.['not-ranged'];
        const reason = key === undefined ? null : say(context, key);
        if (reason !== null) return { type: 'text', value: reason };
      }
      return { type: 'text', value: say(context, NOT_RANGED_KEY) ?? cell.state };
    default: {
      const exhaustive: never = cell;
      throw new Error(`unhandled cell kind: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/** A provenance value as the CSV states it — the ISO instant, the code — in the «Tecnico» sheet. */
function provenanceValue(cell: ExportCell, context: Context): XlsxCell | null {
  switch (cell.kind) {
    case 'number': case 'money': case 'percent': case 'integer': return { type: 'number', value: cell.raw };
    case 'date': return { type: 'text', value: cell.iso };
    case 'text': return cell.value === '' ? null : { type: 'text', value: cell.value };
    case 'label': return { type: 'text', value: cell.value };
    // `not-ranged` is "does not apply" in a provenance column (a plan version's reason when the
    // version is present): nothing to state, so no row.
    case 'missing':
      if (cell.state === 'not-ranged') return null;
      return cell.state === 'counted-zero' ? { type: 'number', value: 0 } : notAvailable(context);
    default: { const x: never = cell; throw new Error(String(x)); }
  }
}

const WIDTH_NUMBERS = new Intl.NumberFormat('en');

function cellLength(cell: XlsxCell): number {
  switch (cell.type) {
    case 'date': return cell.format.length + 2;
    case 'blank': case 'subtotal': return 0;
    case 'number': return WIDTH_NUMBERS.format(cell.value).length;
    case 'text': return cell.value.length;
    default: { const x: never = cell; throw new Error(String(x)); }
  }
}

function widthsOf(header: readonly string[], rows: readonly (readonly XlsxCell[])[]): number[] {
  const sample = rows.slice(0, 200);
  const count = Math.max(header.length, ...sample.map((row) => row.length));
  return Array.from({ length: count }, (_, i) => {
    let widest = header[i]?.length ?? 0;
    for (const row of sample) {
      const cell = row[i];
      if (cell !== undefined) widest = Math.max(widest, cellLength(cell));
    }
    return Math.min(Math.max(widest + 2, 8), 60);
  });
}

/** A column as one of the workbook's sheets heads it — for the «Columns» sheet (`docs/adr/9045`). */
interface HeadedColumn {
  readonly column: ExportColumn;
  readonly header: string;
}

/** How many of a sheet's first rows decide its column widths — and are kept for it, whatever the sheet's size. */
const WIDTH_SAMPLE_ROWS = 200;

/** One data sheet laid out: its business columns, and what it sends to «Tecnico». */
interface LaidOut {
  readonly spec: WorkbookSheet;
  readonly head: XlsxSheetHead;
  /** A row of the sheet's source as the sheet's cells. */
  readonly rowOf: (row: readonly ExportCell[]) => XlsxCell[];
  /** The totals row under `count` rows, or `null` when the sheet has none. */
  readonly totalsOf: (count: number) => XlsxCell[] | null;
  /** The autofilter's range over `count` rows. */
  readonly autoFilterOf: (count: number) => string | null;
  /** The rows the sheet counted, when it read them all; `null` when it read only enough to size its columns. */
  readonly counted: number | null;
  readonly technical: {
    readonly header: readonly string[];
    /** The «Tecnico» row of the source's `r`-th row. */
    readonly rowOf: (row: readonly ExportCell[], r: number) => XlsxCell[];
    /** The first rows of it, for the sheet's column widths. */
    readonly sample: readonly (readonly XlsxCell[])[];
  } | null;
  /** The columns the sheet shows, and the ones it sends to «Tecnico», each with its header as written. */
  readonly guide: { readonly shown: readonly HeadedColumn[]; readonly technical: readonly HeadedColumn[] };
}

/**
 * Lays one data sheet out. Its rows are read ONCE here, to learn what the header must say before
 * the first row is written — whether the sheet's amounts share one currency, which of its cells need a
 * reason in «Tecnico» — and kept only as far as the column widths need: `WIDTH_SAMPLE_ROWS` of them.
 * A sheet with nothing to learn from its rows (no amount, no reason column) stops reading there.
 */
async function layOut(name: string, spec: WorkbookSheet, context: Context, fallbacks: string[]): Promise<LaidOut> {
  const indexed = spec.columns.map((column, index) => ({ column, index }));
  const business = indexed.filter(({ column }) => column.provenance !== true && !isTechnicalColumn(column));
  const technical = indexed.filter(({ column }) => column.provenance !== true && isTechnicalColumn(column));
  const identifies = indexed.filter(({ column }) => column.provenance !== true && column.identifies === true);

  // Why a business cell is absent, where its column says (`absent.reasonKeys`) and a row needs it:
  // one `<key>_absent_reason` column each, in «Tecnico», so the data sheet shows only a blank or «n.d.».
  const reasonOf = (column: ExportColumn, cell: ExportCell | undefined): string | null => {
    if (cell?.kind !== 'missing') return null;
    // A cell that already reads its reason (`notRanged: 'reason'`) is not said twice.
    if (cell.state === 'not-ranged' && column.absent?.notRanged === 'reason') return null;
    const key = column.absent?.reasonKeys?.[cell.state];
    return key === undefined ? null : say(context, key);
  };

  // What the rows have to say about the header: the currencies on the sheet, and the columns whose
  // cells carry a reason.
  const moneyColumns = indexed.filter(({ column }) => column.kind === 'money');
  const reasonColumns = business.filter(({ column }) => column.absent?.reasonKeys !== undefined);
  const needsEveryRow = moneyColumns.length > 0 || reasonColumns.length > 0;
  const currencies = new Set<string>();
  const explainedAt = new Set<number>();
  const sample: (readonly ExportCell[])[] = [];
  let count = 0;
  let readAll = true;
  for await (const row of spec.rows() as AsyncIterable<readonly ExportCell[]>) {
    count += 1;
    if (sample.length < WIDTH_SAMPLE_ROWS) sample.push(row);
    for (const { column, index } of moneyColumns) {
      const cell = row[index];
      if (cell?.kind === 'money') currencies.add(cell.currency);
      else if (column.currency !== undefined) currencies.add(column.currency);
    }
    for (const { column, index } of reasonColumns) {
      if (!explainedAt.has(index) && reasonOf(column, row[index]) !== null) explainedAt.add(index);
    }
    if (!needsEveryRow && sample.length >= WIDTH_SAMPLE_ROWS) { readAll = false; break; }
  }
  const counted = readAll ? count : null;
  if (counted === 0) for (const { column } of moneyColumns) if (column.currency !== undefined) currencies.add(column.currency);
  // One currency on the sheet: said once, in each amount's header. Several: each amount keeps its
  // currency column, as in the CSV.
  const single = currencies.size <= 1 ? ([...currencies][0] ?? null) : null;

  const reported = new Set<string>();
  const headerOf = (column: ExportColumn, withUnit = true): string => {
    const title = column.header ?? translated(context.translate, column.headerKey, column.headerValues);
    if (title === null && !reported.has(column.key)) {
      reported.add(column.key);
      fallbacks.push(`${name}:${column.key}`);
    }
    const base = title ?? column.key;
    const unit = !withUnit ? null : column.kind === 'money' ? single : unitWords(column.unit, context.translate);
    return unit === null ? base : `${base} (${unit})`;
  };
  const currencyHeader = (column: ExportColumn): string => (
    `${headerOf(column, false)} · ${translated(context.translate, 'export.header.currency') ?? 'currency'}`
  );

  const header: string[] = [];
  const positions: number[] = [];
  const notes: XlsxNote[] = [];
  let frozenColumns = 0;
  // The note is attached by HEADER index, not by column index: an amount adds a currency column
  // beside it when the sheet mixes currencies, so the two are not the same list. The currency
  // column gets its own note, for the reason the guide gives it its own row — every header of the
  // sheet is explained, or the explanation is of a different file.
  const note = (at: number, text: string): void => { if (text !== '') notes.push({ at, text }); };
  for (const { column } of business) {
    positions.push(header.length);
    note(header.length, columnMeaning(column, context.translate));
    header.push(headerOf(column));
    if (column.freeze === true) frozenColumns = header.length;
    if (column.kind === 'money' && single === null) {
      note(header.length, context.translate('export.columnGuide.currencyMeaning', { column: column.key }));
      header.push(currencyHeader(column));
    }
  }

  const rowOf = (row: readonly ExportCell[]): XlsxCell[] => {
    const cells: XlsxCell[] = [];
    for (const { column, index } of business) {
      const cell = row[index];
      if (cell === undefined) throw new Error(`row is narrower than the column set: no cell for "${column.key}" at index ${index}`);
      const own = cell.kind === 'money' ? cell.currency : (column.currency ?? null);
      cells.push(peopleCell(cell, column, column.kind === 'money' ? (own ?? single) : null, context));
      if (column.kind === 'money' && single === null) cells.push(own === null ? { type: 'blank' } : { type: 'text', value: own });
    }
    return cells;
  };

  const lastColumn = columnLetter(Math.max(header.length - 1, 0));
  const autoFilterOf = (rows: number): string | null => (header.length === 0 ? null : `A1:${lastColumn}${rows + 1}`);

  const totalled = business.some(({ column }) => column.total === 'sum');
  const totalsOf = (rows: number): XlsxCell[] | null => {
    if (rows === 0 || !totalled) return null;
    const totals: XlsxCell[] = header.map((): XlsxCell => ({ type: 'blank' }));
    business.forEach(({ column }, i) => {
      if (column.total !== 'sum' || !isNumericKind(column)) return;
      const at = positions[i] ?? 0;
      const letter = columnLetter(at);
      totals[at] = { type: 'subtotal', range: `${letter}2:${letter}${rows + 1}`, format: numberFormat(column, single) };
    });
    if (totals[0]?.type === 'blank') totals[0] = { type: 'text', value: translated(context.translate, TOTAL_KEY) ?? 'Total' };
    return totals;
  };

  const explained = reasonColumns
    .filter(({ index }) => explainedAt.has(index))
    .map(({ column, index }) => ({
      column,
      index,
      reason: {
        key: `${column.key}_absent_reason`,
        headerKey: 'export.technical.absentReason',
        headerValues: { column: headerOf(column, false) },
        kind: 'text',
        descriptionKey: 'export.technical.absentReasonMeaning',
        descriptionValues: { column: headerOf(column, false) },
      } satisfies ExportColumn,
    }));

  // The row's number, its id, then the technical columns — the id once, wherever it was declared —
  // then the reasons.
  const keyed = [...identifies, ...technical.filter(({ column }) => column.identifies !== true)];
  let technicalTable: LaidOut['technical'] = null;
  if (technical.length > 0 || explained.length > 0) {
    const technicalRowOf = (row: readonly ExportCell[], r: number): XlsxCell[] => [
      { type: 'number', value: r + 2, format: '0' },
      ...keyed.map(({ column, index }): XlsxCell => {
        const cell = row[index];
        return cell === undefined ? { type: 'blank' } : peopleCell(cell, column, column.currency ?? single, context);
      }),
      ...explained.map(({ column, index }): XlsxCell => {
        const reason = reasonOf(column, row[index]);
        return reason === null ? { type: 'blank' } : { type: 'text', value: reason };
      }),
    ];
    technicalTable = {
      header: [
        translated(context.translate, 'export.technical.row') ?? 'row',
        ...keyed.map(({ column }) => headerOf(column)),
        ...explained.map(({ reason }) => headerOf(reason)),
      ],
      rowOf: technicalRowOf,
      sample: sample.map((row, r) => technicalRowOf(row, r)),
    };
  }

  // The widths are read off the first rows as they will be written — the totals row among them when
  // the sheet is short enough to have it there.
  const widthRows = sample.map(rowOf);
  if (counted !== null && counted < WIDTH_SAMPLE_ROWS) {
    const totals = totalsOf(counted);
    if (totals !== null) widthRows.push(totals);
  }

  return {
    spec,
    head: { name, header, widths: widthsOf(header, widthRows), frozenColumns, notes },
    rowOf,
    totalsOf,
    autoFilterOf,
    counted,
    technical: technicalTable,
    guide: {
      shown: business.map(({ column }) => ({ column, header: headerOf(column) })),
      technical: [
        ...keyed.map(({ column }) => ({ column, header: headerOf(column) })),
        ...explained.map(({ reason }) => ({ column: reason, header: headerOf(reason) })),
      ],
    },
  };
}

/**
 * The run's provenance columns (ADR 9120), ONCE: read from the request's own table — the CSV's —
 * whatever sheets the workbook lays out, because they are the same on every row. A column whose
 * value does not apply (`not-ranged`) has no row.
 */
async function provenanceOnce(request: ExportRequest, context: Context): Promise<[string, XlsxCell][]> {
  const columns = request.columns.map((column, index) => ({ column, index })).filter(({ column }) => column.provenance === true);
  if (columns.length === 0) return [];
  let first: readonly ExportCell[] | undefined;
  for await (const row of request.rows() as AsyncIterable<readonly ExportCell[]>) {
    first = row;
    break;
  }
  if (first === undefined) return [];
  const row = first;
  return columns.flatMap(({ column, index }): [string, XlsxCell][] => {
    const cell = row[index];
    const value = cell === undefined ? null : provenanceValue(cell, context);
    if (value === null) return [];
    return [[translated(context.translate, column.headerKey, column.headerValues) ?? column.key, value]];
  });
}

/** The browser's own time zone — the user's, as far as a file written in the browser can know. */
export function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/**
 * The workbook, planned: every sheet's head — name, header, widths, frozen columns, notes — and the
 * ROWS as a stream of events, in the order they are written. Nothing but the first rows of each sheet
 * is held: a row is laid out when the stream reaches it and let go once the writer has it.
 *
 * Reading the rows takes more than one pass. The first, here, learns what each header must say
 * ({@link layOut}); the second is the stream, which hands each source row over as the sheet's cells
 * and, in the same breath, as its row of «Tecnico». A request's `rows()` is therefore read at least
 * twice, and must give the same rows each time — a streamed file is kept so that it can
 * (`spool.ts`).
 */
export async function planXlsx(request: ExportRequest, options: XlsxOptions): Promise<XlsxPlan> {
  const { translate, locale = 'en-GB', timeZone = localTimeZone() } = options;
  const context = newContext(translate, dateFormatFor(locale), timeZone);
  const specs: readonly WorkbookSheet[] = request.workbook?.sheets
    ?? [{ titleKey: request.titleKey, columns: request.columns, rows: request.rows }];

  const taken = new Set<string>();
  const name = (raw: string, fallback: string): string => {
    const chosen = sheetName(raw, fallback, taken);
    taken.add(chosen);
    return chosen;
  };

  const fallbacks: string[] = [];
  const laidOut: LaidOut[] = [];
  for (const [i, spec] of specs.entries()) {
    const fallback = i === 0 ? (request.id || SHEET_NAMES.data) : `${SHEET_NAMES.data} ${i + 1}`;
    const sheet = name(translated(translate, spec.titleKey) ?? '', fallback);
    laidOut.push(await layOut(sheet, spec, context, fallbacks));
  }

  // «Tecnico»: provenance once, the time zone, then each sheet's technical columns.
  const text = (value: string): XlsxCell => ({ type: 'text', value });
  const prelude: XlsxCell[][] = fileProvenanceRows(request.provenance).map((row): XlsxCell[] => [
    text(translate(row.labelKey)),
    text(row.valueIsKey === true ? translate(row.value) : row.value),
  ]);
  for (const [label, value] of await provenanceOnce(request, context)) prelude.push([text(label), value]);
  prelude.push([
    text(translate('export.provenance.timeZone')),
    text(translate('export.technical.timeZoneLocal', { zone: timeZone })),
  ]);
  const technicalHeader = [translate('export.provenance.field'), translate('export.provenance.value')];
  const technicalRows = [...prelude];
  for (const { head, technical } of laidOut) {
    if (technical === null) continue;
    technicalRows.push([], [text(head.name)], technical.header.map(text), ...technical.sample.map((cells) => [...cells]));
  }
  const technicalHead: XlsxSheetHead = {
    name: name(translate('export.technical.sheet'), SHEET_NAMES.technical),
    header: technicalHeader,
    widths: widthsOf(technicalHeader, technicalRows).map((width, i) => (i === 1 ? Math.max(width, 40) : width)),
    frozenColumns: 0,
    // «Campo» and «Valore»: a two-column list of facts, not a grid of measures. There is no column
    // meaning to attach — each ROW says what it is, in its own first cell.
    notes: [],
  };

  const guide = columnsSheet(request, laidOut, technicalHead.name, context, name);
  const technicalAt = laidOut.length;
  const guideAt = laidOut.length + 1;

  async function* events(): AsyncGenerator<XlsxEvent> {
    for (const cells of prelude) yield { kind: 'row', sheet: technicalAt, cells, bold: false };
    for (const [sheet, laid] of laidOut.entries()) {
      if (laid.technical !== null) {
        yield { kind: 'row', sheet: technicalAt, cells: [], bold: false };
        yield { kind: 'row', sheet: technicalAt, cells: [text(laid.head.name)], bold: true };
        yield { kind: 'row', sheet: technicalAt, cells: laid.technical.header.map(text), bold: true };
      }
      let rows = 0;
      for await (const source of laid.spec.rows() as AsyncIterable<readonly ExportCell[]>) {
        yield { kind: 'row', sheet, cells: laid.rowOf(source), bold: false };
        if (laid.technical !== null) yield { kind: 'row', sheet: technicalAt, cells: laid.technical.rowOf(source, rows), bold: false };
        rows += 1;
      }
      // The first pass chose the header and the «Tecnico» columns from the rows it read: rows that
      // changed between the passes would put cells under headers that do not describe them.
      if (laid.counted !== null && laid.counted !== rows) {
        throw new Error(`the rows of "${laid.head.name}" changed between two reads: ${laid.counted}, then ${rows}`);
      }
      const totals = laid.totalsOf(rows);
      if (totals !== null) yield { kind: 'row', sheet, cells: totals, bold: true };
      yield { kind: 'end', sheet, autoFilter: laid.autoFilterOf(rows) };
    }
    yield { kind: 'end', sheet: technicalAt, autoFilter: null };
    for (const cells of guide.rows) yield { kind: 'row', sheet: guideAt, cells, bold: false };
    yield { kind: 'end', sheet: guideAt, autoFilter: `A1:E${guide.rows.length + 1}` };
  }

  return { sheets: [...laidOut.map(({ head }) => head), technicalHead, guide.head], fallbacks, events };
}

/**
 * The workbook as data: every sheet, header and typed cell, before any file is written. Pure but
 * for the time zone default, so the rules above are tested without unzipping a file. It holds every
 * row — it is for the tests and for small tables; a file is written from {@link planXlsx}.
 */
export async function buildXlsxWorkbook(request: ExportRequest, options: XlsxOptions): Promise<XlsxWorkbook> {
  const plan = await planXlsx(request, options);
  const rows = plan.sheets.map((): XlsxCell[][] => []);
  const bold = plan.sheets.map((): Set<number> => new Set());
  const filters = plan.sheets.map((): string | null => null);
  for await (const event of plan.events()) {
    if (event.kind === 'end') {
      filters[event.sheet] = event.autoFilter;
      continue;
    }
    if (event.bold) bold[event.sheet]!.add(rows[event.sheet]!.length);
    rows[event.sheet]!.push([...event.cells]);
  }
  return {
    sheets: plan.sheets.map((head, i): XlsxSheet => ({
      ...head, rows: rows[i]!, autoFilter: filters[i] ?? null, boldRows: bold[i]!,
    })),
    fallbacks: plan.fallbacks,
  };
}

// ── «Colonne»: the export explains its own columns (`docs/adr/9045`) ─────────────────────────

/** The machine name of a column: its CSV header — the key, its unit beside it (`table.ts`). */
export function machineName(column: ExportColumn): string {
  return column.unit ? `${column.key} (${column.unit})` : column.key;
}

/** What a column means, in the reader's language; empty when the column does not say. */
export function columnMeaning(column: ExportColumn, translate: ExportTranslate): string {
  if (column.descriptionKey === undefined) return '';
  return translated(translate, column.descriptionKey, column.descriptionValues) ?? '';
}

/**
 * What a column means for the guide: its meaning, or — when none is written — the words that say
 * so. A guide row is never blank and never a meaning nobody wrote (ADR 9045, amended 2026-10-15).
 */
export function meaningOrGap(column: ExportColumn, translate: ExportTranslate): string {
  return columnMeaning(column, translate) || translate('export.columnGuide.undescribed');
}

/**
 * The workbook's «Colonne» sheet: one row per column, grouped by the sheet it is on — each data
 * sheet's columns as that sheet heads it, then «Tecnico»'s: the run's provenance (written once)
 * and each data sheet's technical columns. Sheet · header · machine name · unit · meaning. Built
 * from the columns alone: an amount's unit says where its currency is, never which one it is.
 */
function columnsSheet(
  request: ExportRequest, laidOut: readonly LaidOut[], technicalName: string, context: Context,
  name: (raw: string, fallback: string) => string,
): { readonly head: XlsxSheetHead; readonly rows: readonly (readonly XlsxCell[])[] } {
  const { translate } = context;
  const text = (value: string): XlsxCell => (value === '' ? { type: 'blank' } : { type: 'text', value });
  const unitOf = (column: ExportColumn): string => (
    column.kind === 'money' ? translate('export.columnGuide.unit.moneyWorkbook') : (unitWords(column.unit, translate) ?? '')
  );
  const row = (sheet: string, { column, header }: HeadedColumn): XlsxCell[] => [
    text(sheet), text(header), text(machineName(column)), text(unitOf(column)), text(meaningOrGap(column, translate)),
  ];
  const rows: XlsxCell[][] = [];
  for (const { head, guide } of laidOut) rows.push(...guide.shown.map((headed) => row(head.name, headed)));
  const provenance = request.columns.filter((column) => column.provenance === true);
  rows.push(...provenance.map((column) => row(technicalName, {
    column, header: translated(translate, column.headerKey, column.headerValues) ?? column.key,
  })));
  for (const { head, guide } of laidOut) {
    if (guide.technical.length === 0) continue;
    const group = translate('export.columnGuide.technicalOf', { technical: technicalName, sheet: head.name });
    rows.push(...guide.technical.map((headed) => row(group, headed)));
  }
  const header = [
    translate('export.columnGuide.column.sheet'),
    translate('export.columnGuide.column.header'),
    translate('export.columnGuide.column.machine'),
    translate('export.columnGuide.column.unit'),
    translate('export.columnGuide.column.meaning'),
  ];
  return {
    head: {
      name: name(translate('export.columnGuide.sheet'), SHEET_NAMES.columns),
      header,
      widths: widthsOf(header, rows).map((width, i) => (i === 4 ? 100 : width)),
      frozenColumns: 2,
      // The sheet that explains the columns needs no note explaining its own: «Significato» is a
      // column whose every cell is a meaning.
      notes: [],
    },
    rows,
  };
}

// ── the file ─────────────────────────────────────────────────────────────────

/**
 * The workbook as a Blob, written row by row into a zip (`xlsx-package.ts`) on a worker thread where
 * the browser has one. The writer is imported here, on demand, so a planner who never downloads a
 * workbook never loads it.
 */
export async function toXlsxBlob(request: ExportRequest, options: XlsxOptions): Promise<Blob> {
  const plan = await planXlsx(request, options);
  const { writeXlsxPackage } = await import('./xlsx-package');
  return writeXlsxPackage(plan, options.onProgress);
}

/**
 * The workbook as bytes — for the asynchronous >100k-row path, which streams it to the job
 * service, and for the tests, which unzip it and read the sheets back.
 */
export async function toXlsxBuffer(request: ExportRequest, options: XlsxOptions): Promise<ArrayBuffer> {
  const blob = await toXlsxBlob(request, options);
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  // jsdom's Blob has no `arrayBuffer`; FileReader is the portable path.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('could not read the workbook'));
    reader.readAsArrayBuffer(blob);
  });
}
